import { FC, useState, useRef, useEffect } from 'react';

import { useNovaPoshtaSearch } from '../../hooks/useNovaPoshtaSearch';
import { Routes } from '../../models/routes';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

interface Warehouse {
    ref: string;
    number: string;
    short_address: string;
    description: string;
}

interface Props {
    cityRef: string;
    type: 'branch' | 'parcel_locker';
    value: string; // displayed warehouse number or description
    onSelect: (wh: {
        ref: string;
        number: string;
        address: string;
        description: string;
    }) => void;
    onFallbackInput: (value: string) => void;
    onSelectionClear?: () => void;
    error?: string;
    isFallback?: boolean;
    allowManualEntry?: boolean;
}

export const NovaPoshtaWarehouseSelect: FC<Props> = ({
    cityRef,
    type,
    value,
    onSelect,
    onFallbackInput,
    onSelectionClear,
    error,
    isFallback: externalFallback = false,
    allowManualEntry = true,
}) => {
    const [query, setQuery] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const hasInitializedReset = useRef(false);

    const label = type === 'parcel_locker' ? 'Поштомат' : 'Відділення';

    const { results, isLoading, isFallback, search, clear } =
        useNovaPoshtaSearch<Warehouse>({
            url: Routes.ApiNovaPoshtaWarehouses,
            params: { city_ref: cityRef, type },
            minLength: 0,
            enabled: !!cityRef && (allowManualEntry ? !externalFallback : true),
        });

    const useFallback = allowManualEntry && (externalFallback || isFallback);

    useEffect(() => {
        if (!isOpen) {
            setQuery(value);
        }
    }, [isOpen, value]);

    // When cityRef or type changes, reset
    useEffect(() => {
        if (!hasInitializedReset.current) {
            hasInitializedReset.current = true;
            return;
        }

        setQuery('');
        clear();
    }, [cityRef, type, clear]);

    // Load initial list when cityRef is available and not fallback
    useEffect(() => {
        if (cityRef && !useFallback) {
            search('');
        }
    }, [cityRef, type]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (val: string) => {
        setQuery(val);
        if (useFallback) {
            onFallbackInput(val);
        } else {
            if (val !== value) {
                onSelectionClear?.();
            }
            search(val);
            setIsOpen(true);
        }
    };

    const handleSelect = (wh: Warehouse) => {
        setQuery(`${label} №${wh.number}`);
        setIsOpen(false);
        clear();
        onSelect({
            ref: wh.ref,
            number: wh.number,
            address: wh.short_address,
            description: wh.description,
        });
    };

    const handleFocus = () => {
        if (!useFallback && results.length > 0) {
            setIsOpen(true);
        } else if (!useFallback && cityRef) {
            search(query);
            setIsOpen(true);
        }
    };

    const handleBlur = () => {
        window.setTimeout(() => setIsOpen(false), 0);
    };

    return (
        <div ref={wrapperRef} className="relative space-y-1">
            <Label htmlFor="np_warehouse">{label} *</Label>
            <Input
                id="np_warehouse"
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={
                    useFallback
                        ? `Введіть номер ${label.toLowerCase()}у...`
                        : `Пошук ${label.toLowerCase()}у за номером або назвою...`
                }
                className={error ? 'border-red-500' : ''}
                autoComplete="off"
                disabled={!cityRef && !useFallback}
            />
            {isLoading && (
                <p className="text-xs text-muted-foreground">Пошук...</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
            {!allowManualEntry && isFallback && (
                <p className="text-xs text-red-500">
                    Не вдалося завантажити список відділень. Спробуйте ще раз.
                </p>
            )}

            {isOpen && results.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-white shadow-lg dark:border-white/10 dark:bg-slate-900 dark:shadow-2xl">
                    {results.map((wh) => (
                        <li
                            key={wh.ref}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50 dark:text-slate-100 dark:hover:bg-slate-800"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleSelect(wh)}
                        >
                            <span className="font-medium">№{wh.number}</span>
                            <span className="ml-2 text-xs text-muted-foreground">
                                {wh.short_address}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
